import { faSliders } from '@fortawesome/free-solid-svg-icons';
import { generatePath, useNavigate } from 'react-router';

import { OverflowItem, OverflowMenu, OverflowMenuProps } from 'Components/Buttons/OverflowMenu';
import { ColumnConfig, ColumnType } from 'Components/Table/Columns';
import { FilterConfig, FilterType } from 'Components/Table/Components/Filters';
import { Table, TableProps } from 'Components/Table/Table';
import { DueDiligenceWorkflowProgressTableCellContent } from 'Components/UcrmTprm/DueDiligenceWorkflowProgressTableCellContent/DueDiligenceWorkflowProgressTableCellContent';
import { ICON_CALENDAR, ICON_CLOSE, ICON_DELETE_REMOVE, ICON_EDIT_MODIFY_UPDATE } from 'Config/Icons';
import { UCRM_UPDATE_BUSINESS, UCRM_UPDATE_USE_CASE, UCRM_USE_CASE_DASHBOARD } from 'Config/PathTemplates';
import { buildUrlForCompletingIntake } from 'Helpers/UcrmTprm';
import { UCRM_ROLES } from 'Helpers/UserUtils';
import { OrganizationsAndAssessmentSubjectsTableRowData } from 'Models/AssessmentSubject';
import { AssessmentWorkflowState } from 'Models/DueDiligenceWorkflow';
import { ActiveState, FinalizationState, userCanSubmitOrFinalizeDraft, userHasFullAccessOrCreatedDraft } from 'Models/Organization';
import { ProductModule } from 'Models/ProductModule';
import { BusinessResponseWithUseCases, BusinessWithUseCasesForNonUcrmUser, UcrmEntities, UseCase, UseCaseForNonUcrmUser } from 'Models/Ucrm';

import { BusinessAndUseCaseTableExpandedInformation } from './BusinessAndUseCaseTableExpandedInformation';

/**
 * @param onDeleteBusiness Opens the modal for deleting a business.
 * @param onDeleteUseCase Opens the modal for deleting a use case.
 * @param onDisplayUsers Opens a modal listing the given users under the given header.
 * @param onSetActiveStates Opens the modal for setting the active states of a business and its use cases.
 * @param onSetAssessmentDueDate Opens the modal for setting a use case's due diligence workflow due date.
 * @param onSetIrqTargetCompletionDate Opens the modal for setting a use case's inherent risk questionnaire due date.
 * @param userTypeAndTableRowsData The rows to display, plus the logged-in user's ID and whether they have full UCRM access.
 */
export interface BusinessAndUseCaseTableProps {
    onDisplayUsers: (userIds: string[], header: string) => void;
    onDeleteBusiness: (business: BusinessResponseWithUseCases | BusinessWithUseCasesForNonUcrmUser) => void;
    onDeleteUseCase: (useCase: UseCase | UseCaseForNonUcrmUser) => void;
    onSetActiveStates: (business: BusinessResponseWithUseCases) => void;
    onSetAssessmentDueDate: (useCase: UseCase) => void;
    onSetIrqTargetCompletionDate: (useCase: UseCase) => void;
    userTypeAndTableRowsData: { userHasFullUcrmAccess: boolean; userId: string; tableRowsData: OrganizationsAndAssessmentSubjectsTableRowData<UcrmEntities>[] };
}

/**
 * The table on the AI Governance (UCRM) "Businesses and Use Cases" page (`BusinessAndUseCaseListing`), with one row per business/use case pair.
 * A row may have no use case (`serviceData` is `undefined`) when the business has no use cases matching the active-state filter, so every column, filter, and menu option must handle that case.
 * Each row's overflow menu offers only the actions the user may take given the drafts' states and whether the user has full UCRM access or created the drafts; actions that open a modal are delegated to the parent through the `on...` props, while navigation actions are handled here.
 */
export const BusinessAndUseCaseTable = (props: BusinessAndUseCaseTableProps) => {
    const navigate = useNavigate();

    /**
     * Converts a row's progress into a number so that the "Assessment Progress" column can be sorted.
     * Rows without a use case get negative values (based on the business's finalization state), so they sort before every row with a use case.
     */
    const getNumericalValueForRowProgress = (rowData: OrganizationsAndAssessmentSubjectsTableRowData<UcrmEntities>): number => {
        if (!rowData.assessmentSubjectData) {
            // If a row does not have a use case, sort by the business's progress.
            switch (rowData.organization.finalization_state) {
                case FinalizationState.DRAFT_IN_PROGRESS:
                    return -3;
                case FinalizationState.DRAFT_SUBMITTED_TO_BE_FINALIZED:
                    return -2;
                case FinalizationState.FINALIZED:
                    return -1;
            }
        } else if (rowData.assessmentSubjectData.assessmentSubject.last_assessed && rowData.assessmentSubjectData.assessmentSubject.assessment_workflow_data.state === AssessmentWorkflowState.AWAITING_ASSESSMENT) {
            // If the row is for a use case with a completed assessment, the row should drop towards the bottom of the table.
            return rowData.assessmentSubjectData.assessmentSubject.assessment_workflow_setup.due_date ? 100 : 99;
        } else {
            // For use cases in the middle of assessments, we simply sort by the progress on the assessment as represented by the number of checkmarks in the progress cell's hover.
            return rowData.assessmentSubjectData.dueDiligenceWorkflowProgress.completedStepNumber;
        }
    };

    const getOverflowMenuForRow = (rowData: OrganizationsAndAssessmentSubjectsTableRowData<UcrmEntities>) => {
        // The point of returning a discriminated union here is to avoid the need for `as` in the conditionals below that construct the overflow menu options.
        // `business` and `useCase` are "pulled back out", via `const { business, useCase } = calculatedInfo;`, in various spots below so that the callbacks capture the narrowed `useCase` value; TypeScript does not carry a narrowing of `calculatedInfo.useCase` into a callback, because the property could be reassigned before the callback runs.
        const calculatedInfo:
            | {
                  userHasFullUcrmAccess: true;
                  business: BusinessResponseWithUseCases;
                  useCase?: UseCase;
                  businessIsADraft: boolean;
                  useCaseIsADraft: boolean;
                  userCanEditBusiness: true;
                  userCanEditUseCase: true;
                  userCanSubmitOrFinalizeBusiness: boolean;
                  userCanSubmitOrFinalizeUseCase: boolean;
              }
            | {
                  userHasFullUcrmAccess: false;
                  business: BusinessWithUseCasesForNonUcrmUser;
                  useCase?: UseCaseForNonUcrmUser;
                  businessIsADraft: boolean;
                  useCaseIsADraft: boolean;
                  userCanEditBusiness: boolean;
                  userCanEditUseCase: boolean;
                  userCanSubmitOrFinalizeBusiness: boolean;
                  userCanSubmitOrFinalizeUseCase: boolean;
              } = (() => {
            if (rowData.userHasFullAccess) {
                const business = rowData.organization;
                const useCase = rowData.assessmentSubjectData?.assessmentSubject;

                const businessIsADraft = business.finalization_state !== FinalizationState.FINALIZED;
                const useCaseIsADraft = useCase !== undefined && useCase.finalization_state !== FinalizationState.FINALIZED;

                const userCanEditBusiness = true;
                const userCanEditUseCase = true;

                const userCanSubmitOrFinalizeBusiness = userCanSubmitOrFinalizeDraft(rowData.userId, rowData.userHasFullAccess, business);
                const userCanSubmitOrFinalizeUseCase = useCase !== undefined && userCanSubmitOrFinalizeDraft(rowData.userId, rowData.userHasFullAccess, useCase);

                return { userHasFullUcrmAccess: true, business, useCase, businessIsADraft, useCaseIsADraft, userCanEditBusiness, userCanEditUseCase, userCanSubmitOrFinalizeBusiness, userCanSubmitOrFinalizeUseCase };
            } else {
                const business = rowData.organization;
                const useCase = rowData.assessmentSubjectData?.assessmentSubject;

                const businessIsADraft = business.finalization_state !== FinalizationState.FINALIZED;
                const useCaseIsADraft = useCase !== undefined && useCase.finalization_state !== FinalizationState.FINALIZED;

                const userCanEditBusiness = userHasFullAccessOrCreatedDraft(rowData.userId, rowData.userHasFullAccess, business);
                const userCanEditUseCase = useCase !== undefined && userHasFullAccessOrCreatedDraft(rowData.userId, rowData.userHasFullAccess, useCase);

                const userCanSubmitOrFinalizeBusiness = userCanSubmitOrFinalizeDraft(rowData.userId, rowData.userHasFullAccess, business);
                const userCanSubmitOrFinalizeUseCase = useCase !== undefined && userCanSubmitOrFinalizeDraft(rowData.userId, rowData.userHasFullAccess, useCase);

                return { userHasFullUcrmAccess: false, business, useCase, businessIsADraft, useCaseIsADraft, userCanEditBusiness, userCanEditUseCase, userCanSubmitOrFinalizeBusiness, userCanSubmitOrFinalizeUseCase };
            }
        })();

        const overflowItems: OverflowItem[] = [];

        const intakeIsInProgress = calculatedInfo.businessIsADraft || !calculatedInfo.useCase || calculatedInfo.useCaseIsADraft;
        // Users with full access can also complete the intake to add a use case to a business that has none. Users without full access can only complete the intake when something remains for them to submit; once they have submitted their drafts, they can still manage (edit) them, but submitting them again would fail.
        const userCanCompleteIntake = calculatedInfo.userCanSubmitOrFinalizeBusiness || calculatedInfo.userCanSubmitOrFinalizeUseCase || (calculatedInfo.userHasFullUcrmAccess && !calculatedInfo.useCase);

        if (userCanCompleteIntake) {
            const { business, useCase } = calculatedInfo;
            if (useCase && calculatedInfo.userCanEditUseCase) {
                overflowItems.push({
                    text: 'Complete Intake',
                    onClickAction: () => navigate(buildUrlForCompletingIntake(ProductModule.UCRM, business.id, useCase.id)),
                    icon: ICON_EDIT_MODIFY_UPDATE,
                });
            } else {
                overflowItems.push({
                    text: 'Complete Intake',
                    onClickAction: () => navigate(buildUrlForCompletingIntake(ProductModule.UCRM, business.id)),
                    icon: ICON_EDIT_MODIFY_UPDATE,
                });
            }
        } else {
            const { business, useCase } = calculatedInfo;
            if (calculatedInfo.userCanEditBusiness) {
                overflowItems.push({
                    text: 'Manage Business',
                    onClickAction: () => navigate(generatePath(UCRM_UPDATE_BUSINESS, { thirdPartyId: business.id })),
                    icon: ICON_EDIT_MODIFY_UPDATE,
                });
            }

            if (useCase && calculatedInfo.userCanEditUseCase) {
                overflowItems.push({
                    text: 'Manage Use Case',
                    onClickAction: () => navigate(generatePath(UCRM_UPDATE_USE_CASE, { thirdPartyId: business.id, serviceId: useCase.id })),
                    icon: ICON_EDIT_MODIFY_UPDATE,
                });
            }
        }

        if (calculatedInfo.userHasFullUcrmAccess && calculatedInfo.useCase) {
            const { useCase } = calculatedInfo;
            overflowItems.push({
                text: 'Set Inherent Risk Questionnaire Due Date',
                onClickAction: () => props.onSetIrqTargetCompletionDate(useCase),
                icon: ICON_CALENDAR,
            });
        }

        if (calculatedInfo.userHasFullUcrmAccess && calculatedInfo.useCase) {
            const { useCase } = calculatedInfo;
            overflowItems.push({
                text: 'Set Due Diligence Workflow Due Date',
                onClickAction: () => props.onSetAssessmentDueDate(useCase),
                icon: ICON_CALENDAR,
            });
        }

        if (intakeIsInProgress) {
            if (calculatedInfo.userCanEditBusiness) {
                overflowItems.push({
                    text: 'Delete Business',
                    onClickAction: () => props.onDeleteBusiness(calculatedInfo.business),
                    icon: ICON_DELETE_REMOVE,
                });
            }

            const { useCase } = calculatedInfo;
            if (useCase && calculatedInfo.userCanEditUseCase) {
                overflowItems.push({
                    text: 'Delete Use Case',
                    onClickAction: () => props.onDeleteUseCase(useCase),
                    icon: ICON_DELETE_REMOVE,
                });
            }
        }

        if (calculatedInfo.userHasFullUcrmAccess) {
            overflowItems.push({
                text: 'Toggle active/inactive statuses',
                onClickAction: () => props.onSetActiveStates(calculatedInfo.business),
                icon: faSliders,
            });
        }

        if (overflowItems.length === 0) {
            overflowItems.push({
                text: 'Close Menu (no actions available)',
                onClickAction: () => {},
                icon: ICON_CLOSE,
            });
        }

        const overflowMenuProps: OverflowMenuProps = {
            overflowItems: overflowItems,
            accessibilityTitle: calculatedInfo.useCase ? `Open menu for ${calculatedInfo.business.name}: ${calculatedInfo.useCase.name}` : `Open menu for ${calculatedInfo.business.name}`,
        };

        return <OverflowMenu {...overflowMenuProps} />;
    };

    const getSecondaryString = (businessOrUseCase: { active_state: ActiveState; finalization_state: FinalizationState } | undefined): string | undefined => {
        if (businessOrUseCase === undefined) {
            return undefined;
        }

        let combinedStatesString = '';
        if (businessOrUseCase.active_state !== ActiveState.ACTIVE) {
            combinedStatesString += businessOrUseCase.active_state;
        }
        if (businessOrUseCase.finalization_state !== FinalizationState.FINALIZED) {
            combinedStatesString += combinedStatesString === '' ? businessOrUseCase.finalization_state : `, ${businessOrUseCase.finalization_state}`;
        }
        return combinedStatesString === '' ? undefined : combinedStatesString;
    };

    const columns: ColumnConfig<OrganizationsAndAssessmentSubjectsTableRowData<UcrmEntities>>[] = [
        {
            type: ColumnType.PRIMARY_AND_SECONDARY_STRING,
            name: 'Business',
            getPrimaryString: (data) => data.organization.name,
            getSecondaryString: (data) => getSecondaryString(data.organization),
        },
        {
            type: ColumnType.PRIMARY_AND_SECONDARY_STRING,
            name: 'Use Case',
            getPrimaryString: (data) => data.assessmentSubjectData?.assessmentSubject.name,
            getSecondaryString: (data) => getSecondaryString(data.assessmentSubjectData?.assessmentSubject),
            getOnClickPrimaryTextAction: (data) => (props.userTypeAndTableRowsData.userHasFullUcrmAccess && data.assessmentSubjectData && data.assessmentSubjectData.assessmentSubject.finalization_state === FinalizationState.FINALIZED ? { goToLocation: generatePath(UCRM_USE_CASE_DASHBOARD, { third_party_id: data.organization.id, service_id: data.assessmentSubjectData.assessmentSubject.id }) } : undefined),
        },
        {
            type: ColumnType.CUSTOM,
            name: 'Assessment Progress',
            getTableCellContent: (data) => <DueDiligenceWorkflowProgressTableCellContent rowData={data} />,
            getValue: (data) => getNumericalValueForRowProgress(data),
            sortComparator: (a, b) => a - b,
        },
        {
            type: ColumnType.RISK_RATING,
            name: 'Inherent Risk',
            getRiskRating: (data) => (data.assessmentSubjectData ? (data.assessmentSubjectData.assessmentSubject.inherent_risk_score ?? 0) : undefined),
        },
        {
            type: ColumnType.RISK_RATING,
            name: 'Residual Risk',
            getRiskRating: (data) => (data.assessmentSubjectData ? (data.assessmentSubjectData.assessmentSubject.residual_risk_score ?? 0) : undefined),
        },
        {
            type: ColumnType.DATE,
            name: 'Due Diligence Workflow Due Date',
            getDateString: (data) => data.assessmentSubjectData?.assessmentSubject.assessment_workflow_setup.due_date,
        },
    ];

    const filters: FilterConfig<OrganizationsAndAssessmentSubjectsTableRowData<UcrmEntities>>[] = [
        {
            type: FilterType.USERS,
            id: 'creatorFilter',
            label: 'Filter by Creator',
            getUserIdsForRow: (data) => {
                const userIds: string[] = [];
                if (data.organization.created_by) {
                    userIds.push(data.organization.created_by);
                }
                if (data.assessmentSubjectData?.assessmentSubject.created_by) {
                    userIds.push(data.assessmentSubjectData.assessmentSubject.created_by);
                }
                return userIds;
            },
            defaultSelections: props.userTypeAndTableRowsData.userHasFullUcrmAccess ? [] : [props.userTypeAndTableRowsData.userId],
        },
        {
            type: FilterType.USERS,
            id: 'managerFilter',
            label: 'Filter by Manager',
            rolesRestriction: UCRM_ROLES,
            getUserIdsForRow: (data) => {
                const userIds: string[] = [data.organization.vendor_manager_user_id];
                if (data.assessmentSubjectData?.assessmentSubject) {
                    userIds.push(data.assessmentSubjectData.assessmentSubject.vendor_service_manager_user_id);
                }
                return userIds;
            },
        },
        {
            type: FilterType.SINGLE_PROPERTY,
            id: 'statusFilter',
            label: 'Filter by Draft Status',
            options: Object.values(FinalizationState).map((state) => ({
                name: state,
                value: state,
            })),
            getFilterableDataForRow: (data) => {
                const statuses: FinalizationState[] = [data.organization.finalization_state];
                if (data.assessmentSubjectData?.assessmentSubject) {
                    statuses.push(data.assessmentSubjectData.assessmentSubject.finalization_state);
                }
                return statuses;
            },
            canSelectMultiple: true,
        },
        {
            type: FilterType.TAGS,
            id: 'tagsFilter',
            label: 'Filter by Tags',
            getTagIdsForRow: (data) => {
                const businessTagIds = [...data.organization.tags];
                const useCaseTagIds = data.assessmentSubjectData ? [...data.assessmentSubjectData.assessmentSubject.tags] : [];
                return [...businessTagIds, ...useCaseTagIds];
            },
        },
    ];

    const tableProps: TableProps<OrganizationsAndAssessmentSubjectsTableRowData<UcrmEntities>> = {
        columns,
        filters,
        defaultSortColumn: 'Business',
        rowsData: props.userTypeAndTableRowsData.tableRowsData,
        getUniqueIdForRow: (data) => (data.assessmentSubjectData ? `${data.organization.id}${data.assessmentSubjectData.assessmentSubject.id}` : data.organization.id),
        getExpandableElementForRow: (data) => <BusinessAndUseCaseTableExpandedInformation business={data.organization} folders={data.foldersByOrganizationId?.get(data.organization.id) ?? []} onDisplayUsers={props.onDisplayUsers} useCase={data.assessmentSubjectData?.assessmentSubject} userHasFullUcrmAccess={props.userTypeAndTableRowsData.userHasFullUcrmAccess} />,
        getOverflowMenuForRow,
    };

    return <Table {...tableProps} />;
};
